import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Calendar, User, Search, UserPlus, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image_url: string | null;
  category: string;
  author: string;
  published_at: string;
  published?: boolean;
}

const Blog = () => {
  const { t, language } = useLanguage();
  const { isBlogEditor, isAdmin } = useAuth();
  const { toast } = useToast();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const canAddContent = isBlogEditor || isAdmin;

  useEffect(() => {
    fetchPosts();
  }, [language]);

  useEffect(() => {
    filterPosts();
  }, [searchTerm, selectedCategory, posts]);

  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      
      // Fetch posts with translations
      // If user is editor/admin, show all posts including drafts; otherwise only published
      let query = supabase
        .from('blog_posts')
        .select(`
          *,
          blog_posts_translations (*)
        `)
        .order('published_at', { ascending: false });
      
      // Only filter by published if user is not an editor/admin
      if (!canAddContent) {
        query = query.eq('published', true);
      }

      const { data: postsData, error } = await query;

      if (error) throw error;

      const transformedPosts = postsData?.map((post: any) => {
        const translation = post.blog_posts_translations?.find(
          (t: any) => t.language === language
        );

        return {
          id: post.id,
          slug: post.slug,
          title: translation?.title || post.title,
          excerpt: translation?.excerpt || post.excerpt,
          featured_image_url: post.featured_image_url,
          category: post.category,
          author: translation?.author || post.author,
          published_at: post.published_at,
          published: post.published,
        };
      }) || [];

      setPosts(transformedPosts);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      toast({
        title: t("blog.error"),
        description: t("blog.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = posts;

    if (searchTerm) {
      filtered = filtered.filter((post) =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }

    setFilteredPosts(filtered);
  };

  const categories = Array.from(new Set(posts.map((post) => post.category)));

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <h1 className="text-4xl font-bold flex items-center gap-2">
                <BookOpen className="h-8 w-8" />
                {t("blog.title")}
              </h1>
              {canAddContent && (
                <Link to="/admin?tab=blog&action=add">
                  <Button size="sm" className="gap-2">
                    <Plus className="h-4 w-4" />
                    {t("blog.addPost")}
                  </Button>
                </Link>
              )}
            </div>
            <p className="text-lg text-muted-foreground">
              {t("blog.subtitle")}
            </p>
          </div>

          {/* Search and Filter */}
          <div className="mb-8 space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t("blog.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategory === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(null)}
                >
                  {t("blog.allCategories")}
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Blog Posts Grid */}
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("blog.loading")}</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">{t("blog.noPosts")}</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow relative">
                    {post.featured_image_url && (
                      <div className="aspect-video overflow-hidden rounded-t-lg">
                        <img
                          src={post.featured_image_url}
                          alt={post.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{post.category}</Badge>
                        {canAddContent && !post.published && (
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                            Draft
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                      <CardDescription className="line-clamp-3">
                        {post.excerpt}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {post.author}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.published_at).toLocaleDateString(
                            language === "ru" ? "ru-RU" : language === "es" ? "es-ES" : "en-US"
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Become a Contributor CTA - Only show if not already a contributor */}
          {!canAddContent && (
            <Card className="mt-12 bg-primary/5 border-primary/20">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <UserPlus className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-2xl">{t("blog.contribute.title")}</CardTitle>
                <CardDescription className="text-base">
                  {t("blog.contribute.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center pb-6">
                <Link to="/become-contributor">
                  <Button size="lg" className="gap-2">
                    <UserPlus className="h-5 w-5" />
                    {t("blog.contribute.button")}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
